/**
 * Date-window rules shared by the filter bar and the API.
 *
 * Both sides import the same cap and the same notion of "today" (IST), so the
 * UI never offers a window the API refuses. The API still validates: a
 * hand-edited URL is a real request.
 */

export const REPORT_TZ = "Asia/Kolkata";

/** Longest window a tab will scan. A cost ceiling, not a data limit. */
export const MAX_WINDOW_DAYS = 366;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value) {
  if (typeof value !== "string" || !ISO_DATE.test(value)) return false;
  const t = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === value;
}

export function addDays(iso, n) {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}

/** Whole days from `from` to `to` (negative if inverted). */
export function daysBetween(from, to) {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** Today's date in IST. A Worker has no local timezone, so UTC "today" is wrong for 5.5h a day. */
export function istToday(now = new Date()) {
  return new Date(now.getTime() + 330 * 60_000).toISOString().slice(0, 10);
}

/** Why a window is unusable, or null if it is fine. */
export function windowProblem(from, to, today) {
  if (daysBetween(from, to) < 0) return "inverted";
  if (daysBetween(from, to) + 1 > MAX_WINDOW_DAYS) return "too-wide";
  if (daysBetween(today, to) > 0) return "future";
  return null;
}

export function windowProblemMessage(problem) {
  switch (problem) {
    case "inverted":
      return "The start date is after the end date.";
    case "too-wide":
      return `Windows are capped at ${MAX_WINDOW_DAYS} days.`;
    case "future":
      return "The end date is in the future.";
    default:
      return "";
  }
}

/** Coerce any pair of dates into a legal window (future → today, inverted → single day, too wide → trimmed from the start). */
export function clampWindow(from, to, today) {
  const end = daysBetween(today, to) > 0 ? today : to;
  if (daysBetween(from, end) < 0) return { from: end, to: end };
  const floor = addDays(end, -(MAX_WINDOW_DAYS - 1));
  return { from: daysBetween(floor, from) < 0 ? floor : from, to: end };
}

/** Preset windows offered by the date picker. Each returns { from, to }. */
export const DATE_PRESETS = [
  { id: "7d", label: "Last 7 days", range: (today) => ({ from: addDays(today, -6), to: today }) },
  { id: "28d", label: "Last 28 days", range: (today) => ({ from: addDays(today, -27), to: today }) },
  { id: "90d", label: "Last 90 days", range: (today) => ({ from: addDays(today, -89), to: today }) },
  {
    id: "mtd",
    label: "Month to date",
    range: (today) => ({ from: `${today.slice(0, 8)}01`, to: today }),
  },
  {
    id: "ytd",
    label: "Year to date",
    range: (today) => ({ from: `${today.slice(0, 4)}-01-01`, to: today }),
  },
  { id: "365d", label: "Last 12 months", range: (today) => ({ from: addDays(today, -364), to: today }) },
];

export const DEFAULT_PRESET = "28d";
